'use client';

import Image from 'next/image';
import { motion } from 'motion/react';
import ServiceCard from '@/components/sections/services/card';
import { SectionLayout } from '@/components/shared/section-layout';
import { useServiceMotionVariants, useSectionMotionVariants } from '@/components/shared/motion';
import { useScrollRevealInView } from '@/hooks/use-scroll-reveal-motion';
import { SanityInlineText } from '@/components/shared/sanity-text';
import SectionHeader from '@/components/shared/section-header';
import { SectionCtaPair, sectionCtaPairFromContent } from '@/components/shared/section-cta-pair';
import type { ServicesSectionContent } from '@/sanity/lib/sections';

const SERVICE_CARD_IMAGE_SIZES =
  '(max-width: 767px) calc(100vw - 2rem), (max-width: 1279px) calc((100vw - 3rem) / 2), 411px';
const SERVICE_CARD_IMAGE_CLASS = 'object-cover';

export default function Services({ content }: { content?: ServicesSectionContent | null }) {
  const { servicesGridRevealVariants, servicesTileRevealVariants } = useServiceMotionVariants();
  const { sectionSequenceBlockVariants, sectionSequenceRevealVariants } =
    useSectionMotionVariants();
  const { ref: revealRef, ...scrollReveal } = useScrollRevealInView();

  if (!content || content.show === false) return null;

  const { header } = content;
  const cards = content.cards ?? [];
  const { primary, secondary } = sectionCtaPairFromContent(content.showCTA, content.ctaButtons);

  return (
    <SectionLayout
      id="services-we-offer"
      className="relative scroll-mt-14 sm:scroll-mt-0 bg-white dark:bg-neutral-950 py-6 sm:py-12 xl:py-16 2xl:py-24"
      containerClassName="mx-auto max-w-7xl px-4 sm:px-6 min-[1324px]:px-0"
    >
      <motion.div
        ref={revealRef}
        {...scrollReveal}
        variants={sectionSequenceRevealVariants}
        className="space-y-6 sm:space-y-12"
      >
        {content.showHeader !== false ? (
          <SectionHeader
            sequenceChild
            content={{
              eyebrow: header?.tagline,
              description: header?.subheading,
            }}
            titleSlot={
              <h2 className="text-3xl sm:text-5xl leading-tight font-light tracking-tight text-foreground">
                <SanityInlineText
                  blocks={header?.heading}
                  emphasisClassName="italic font-normal text-[#948B7F] dark:text-[#FCFAD7]"
                />
              </h2>
            }
            eyebrowClassName="text-[#948B7F] dark:text-[#FCFAD7]"
            emphasisClassName="text-[#948B7F] dark:text-[#FCFAD7]"
            headingClassName="text-foreground"
            subheadingClassName="text-muted-foreground"
          />
        ) : null}
        {primary ? (
          <motion.div variants={sectionSequenceBlockVariants}>
            <SectionCtaPair primary={primary} secondary={secondary} trackingLocation="services" />
          </motion.div>
        ) : null}
        {content.showCards !== false && cards.length > 0 ? (
          <motion.div
            variants={servicesGridRevealVariants}
            className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3"
          >
            {cards.map((service, index) => (
              <ServiceCard
                key={`${service.headerTitle}-${index}`}
                headerTitle={service.headerTitle}
                headerDescription={service.headerDescription}
                revealVariants={servicesTileRevealVariants}
                media={
                  <Image
                    src={service.src}
                    alt={service.alt}
                    fill
                    sizes={SERVICE_CARD_IMAGE_SIZES}
                    className={SERVICE_CARD_IMAGE_CLASS}
                  />
                }
              />
            ))}
          </motion.div>
        ) : null}
      </motion.div>
    </SectionLayout>
  );
}
